import type { FunctionComponent } from 'react';
import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import { Analytics, formatContainers } from '@trello/atlassian-analytics';
import { intl } from '@trello/i18n';
import { localizeServerError } from '@trello/legacy-i18n';
import { Button } from '@trello/nachos/button';
import { showFlag } from '@trello/nachos/experimental-flags';
import { CloseIcon } from '@trello/nachos/icons/close';
import { Popover, usePopover } from '@trello/nachos/popover';
import { navigate } from '@trello/router/navigate';

import { PopoverConfirm } from 'app/src/components/PopoverConfirm';
import { useLeaveWorkspaceMutation } from './LeaveWorkspaceMutation.generated';

import styles from './WorkspaceMemberLeaveButton.less';

export const WorkspaceMemberLeaveButton: FunctionComponent<{
  workspaceId: string;
  memberId: string;
}> = ({ workspaceId, memberId }) => {
  const onShow = useCallback(() => {
    Analytics.sendScreenEvent({
      name: 'leaveWorkspaceInlineDialog',
    });
  }, []);

  const { triggerRef, toggle, popoverProps, hide } =
    usePopover<HTMLButtonElement>({
      onShow,
    });

  const [leaveWorkspace] = useLeaveWorkspaceMutation();

  const onClick = useCallback(() => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'leaveWorkspaceButton',
      source: 'workspaceMembersScreen',
      containers: formatContainers({
        workspaceId,
      }),
    });

    toggle();
  }, [toggle, workspaceId]);

  const onLeaveConfirm = useCallback(async () => {
    const source = 'workspaceMembersScreen';
    const taskName = 'edit-organization/members/remove';
    const traceId = Analytics.startTask({ taskName, source });

    Analytics.sendClickedButtonEvent({
      buttonName: 'leaveWorkspaceConfirmButton',
      source,
      containers: formatContainers({
        workspaceId,
      }),
      attributes: {
        taskId: traceId,
      },
    });

    try {
      await leaveWorkspace({
        variables: {
          member: { id: memberId },
          workspaceId,
          traceId,
        },
      });

      Analytics.sendTrackEvent({
        action: 'removed',
        actionSubject: 'member',
        actionSubjectId: memberId,
        source,
        containers: formatContainers({
          workspaceId,
        }),
        attributes: {
          taskId: traceId,
        },
      });
      Analytics.taskSucceeded({
        taskName,
        traceId,
        source,
      });

      navigate('/', { trigger: true, replace: true });
    } catch (error) {
      showFlag({
        id: 'organization-members',
        title: localizeServerError((error as Error).message),
        appearance: 'error',
        isAutoDismiss: true,
        msTimeout: 3000,
      });
      Analytics.taskFailed({
        taskName,
        traceId,
        source,
        error,
      });
    }
    hide();
  }, [workspaceId, hide, leaveWorkspace, memberId]);

  const popoverTitle = intl.formatMessage({
    id: 'templates.org_remove_or_deactivate_member.leave-organization',
    description: 'Title for the Leave Workspace confirmation popover',
    defaultMessage: 'Leave Workspace',
  });

  return (
    <>
      <Button
        onClick={onClick}
        ref={triggerRef}
        className={styles.button}
        iconBefore={<CloseIcon />}
      >
        <FormattedMessage
          id="templates.member_list_item_detail.leave-ellipsis"
          description="Button to leave workspace"
          defaultMessage="Leave…"
        />
      </Button>
      <Popover {...popoverProps} title={popoverTitle}>
        <PopoverConfirm
          isDanger
          confirmKey={'leave org'}
          confirmCallback={onLeaveConfirm}
        />
      </Popover>
    </>
  );
};
